/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { ref, set } from "firebase/database";
import { db } from "@/config/firebase";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Native (Capacitor) push-token registration.
 *
 * Android: @capacitor/push-notifications returns an FCM registration token
 * which is written to `users/{uid}/fcmToken` — the same RTDB path the web
 * build uses, so functions/src/nudgeLogic.ts & safeCircleNotifications.ts do
 * not need to branch by platform. Asks for user notification permission
 * because Android displays visible notifications via this path.
 *
 * iOS APNs registration is NO LONGER handled here — it moved to
 * useApnsRegistration, which registers pre-auth (Bug 1: the phone-first flow
 * sends the first OTP before any user exists, so APNs can't wait for `user`).
 * This hook now owns only the Android FCM path, which legitimately needs a
 * signed-in user to write the token to /users/{uid}/fcmToken.
 */
export function useNativePushToken() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    const platform = Capacitor.getPlatform();
    // iOS APNs is owned by useApnsRegistration (pre-auth). Here we only do the
    // Android FCM token capture, which needs the authed uid for the RTDB write.
    if (platform !== "android") return;

    let cancelled = false;
    let removeListeners: (() => void) | undefined;

    (async () => {
      try {
        const { PushNotifications } = await import("@capacitor/push-notifications");

        // Android path — visible push, FCM token capture.
        const permStatus = await PushNotifications.checkPermissions();
        let granted = permStatus.receive === "granted";
        if (!granted) {
          const req = await PushNotifications.requestPermissions();
          granted = req.receive === "granted";
        }
        if (!granted || cancelled) return;

        const onRegistration = await PushNotifications.addListener(
          "registration",
          async (token) => {
            if (cancelled) return;
            try {
              await set(ref(db, `users/${user.uid}/fcmToken`), token.value);
            } catch (err) {
              console.warn("[useNativePushToken] failed to persist token:", err);
            }
          },
        );

        const onError = await PushNotifications.addListener(
          "registrationError",
          (err) => {
            console.warn("[useNativePushToken] registrationError:", err);
          },
        );

        await PushNotifications.register();

        removeListeners = () => {
          void onRegistration.remove();
          void onError.remove();
        };
      } catch (err) {
        console.warn("[useNativePushToken] setup failed:", err);
      }
    })();

    return () => {
      cancelled = true;
      removeListeners?.();
    };
  }, [user]);
}
